import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

/**
 * Dragging one meal onto another (§34).
 *
 * The gesture has to work with a finger on a tablet propped against the wall
 * and with a mouse on a laptop, which rules out the browser's own drag-and-
 * drop: it does not exist on touch. So this is built on pointer events, which
 * are the same events for both.
 *
 * Two ways in, because a drag is a poor gesture on a small screen with a long
 * week on it — the day you want may be two swipes away:
 *
 *   drag — hold the grip and move; the meal follows the finger and the sitting
 *          under it lights up.
 *   tap  — tap the grip and it is picked up; tap the grip of another sitting
 *          and the two change places. Nothing is dragged anywhere, which is
 *          also what makes the whole thing usable from a keyboard.
 *
 * What can be dropped where is decided here only loosely — a dinner may swap
 * with a lunch because both are meals, but not with a pudding. The real ruling
 * belongs to the planner and comes back from the server; this exists so that
 * the card under your finger does not light up when nothing could come of it.
 */

/** The sitting types that can be exchanged, by what kind of food they take. */
const SLOT_COURSE: Record<string, string> = {
  breakfast: 'breakfast',
  lunch: 'main',
  dinner: 'main',
  morning_snack: 'morning_snack',
  gouter: 'gouter',
  dessert: 'dessert',
  evening_drink: 'drink',
  aperitif_nibble: 'nibble',
};

export const courseOfSlot = (slotType: string): string => SLOT_COURSE[slotType] ?? slotType;

export interface Draggable {
  assignmentKey: string;
  slotType: string;
  /** What to show under the finger while it is being carried. */
  name: string;
}

/** How far a finger may wander before it is a drag rather than a tap. */
const DRAG_THRESHOLD = 6;
/** How close to the edge of the screen the finger has to be to scroll it. */
const EDGE = 72;
const EDGE_SPEED = 14;

export interface SwapDrag {
  /** The meal being carried, whether by drag or by tap. */
  held: Draggable | null;
  /** The sitting under the finger, if it is one this could be dropped on. */
  over: string | null;
  /** True while a finger is actually moving; false when a meal is held by tap. */
  dragging: boolean;
  point: { x: number; y: number } | null;
  gripProps: (dish: Draggable) => {
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLElement>) => void;
    onPointerCancel: () => void;
    onClick: () => void;
  };
  /** Is this sitting a place the held meal could go? */
  canDropOn: (dish: { assignmentKey: string; slotType: string }) => boolean;
  cancel: () => void;
}

export function useSwapDrag(
  swap: (fromKey: string, toKey: string) => void,
  options: { enabled?: boolean } = {},
): SwapDrag {
  const enabled = options.enabled ?? true;
  const [held, setHeld] = useState<Draggable | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);

  const start = useRef<{ x: number; y: number; dish: Draggable } | null>(null);
  const moved = useRef(false);
  const handledByPointer = useRef(false);
  const heldRef = useRef<Draggable | null>(null);
  heldRef.current = held;

  const cancel = useCallback(() => {
    start.current = null;
    moved.current = false;
    setHeld(null);
    setOver(null);
    setDragging(false);
    setPoint(null);
  }, []);

  /** The sitting under the point, if the held meal could go there. */
  const targetAt = useCallback((x: number, y: number): string | null => {
    const carried = heldRef.current;
    if (!carried) return null;
    const element = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-dish-key]');
    if (!element) return null;
    const key = element.dataset.dishKey;
    if (!key || key === carried.assignmentKey) return null;
    if (element.dataset.dishDroppable !== 'true') return null;
    return element.dataset.dishCourse === courseOfSlot(carried.slotType) ? key : null;
  }, []);

  // A week is longer than a screen, so a drag has to be able to reach the far
  // end of it. While the finger sits near the top or bottom, the page moves.
  useEffect(() => {
    if (!dragging || !point) return;
    const top = point.y < EDGE;
    const bottom = point.y > window.innerHeight - EDGE;
    if (!top && !bottom) return;

    let frame = 0;
    const step = () => {
      window.scrollBy(0, top ? -EDGE_SPEED : EDGE_SPEED);
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [dragging, point]);

  // Anywhere else, or Escape, puts it back down.
  useEffect(() => {
    if (!held) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [held, cancel]);

  const gripProps = useCallback(
    (dish: Draggable) => ({
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (!enabled) return;
        start.current = { x: event.clientX, y: event.clientY, dish };
        moved.current = false;
        event.currentTarget.setPointerCapture?.(event.pointerId);
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        const origin = start.current;
        if (!origin) return;
        const far =
          Math.abs(event.clientX - origin.x) > DRAG_THRESHOLD ||
          Math.abs(event.clientY - origin.y) > DRAG_THRESHOLD;
        if (!moved.current && !far) return;

        if (!moved.current) {
          moved.current = true;
          heldRef.current = origin.dish;
          setHeld(origin.dish);
          setDragging(true);
        }
        setPoint({ x: event.clientX, y: event.clientY });
        setOver(targetAt(event.clientX, event.clientY));
      },
      onPointerUp: (event: PointerEvent<HTMLElement>) => {
        const origin = start.current;
        start.current = null;
        handledByPointer.current = true;
        window.setTimeout(() => {
          handledByPointer.current = false;
        }, 0);
        if (!origin) return;

        if (moved.current) {
          const target = targetAt(event.clientX, event.clientY);
          if (target) swap(origin.dish.assignmentKey, target);
          cancel();
          return;
        }

        // A tap: pick this one up, put it down again, or swap it with whatever
        // is already being carried.
        const carried = heldRef.current;
        if (carried && carried.assignmentKey !== dish.assignmentKey) {
          if (courseOfSlot(carried.slotType) === courseOfSlot(dish.slotType)) {
            swap(carried.assignmentKey, dish.assignmentKey);
          }
          cancel();
          return;
        }
        if (carried && carried.assignmentKey === dish.assignmentKey) {
          cancel();
          return;
        }
        heldRef.current = dish;
        setHeld(dish);
        setDragging(false);
        setPoint(null);
      },
      onPointerCancel: cancel,
      // Keyboards raise a click with no pointer behind it, and that is the
      // whole of the keyboard support: enter picks up, enter puts down.
      onClick: () => {
        if (handledByPointer.current || !enabled) return;
        const carried = heldRef.current;
        if (carried && carried.assignmentKey !== dish.assignmentKey) {
          if (courseOfSlot(carried.slotType) === courseOfSlot(dish.slotType)) {
            swap(carried.assignmentKey, dish.assignmentKey);
          }
          cancel();
          return;
        }
        if (carried) {
          cancel();
          return;
        }
        heldRef.current = dish;
        setHeld(dish);
      },
    }),
    [cancel, enabled, swap, targetAt],
  );

  const canDropOn = useCallback(
    (dish: { assignmentKey: string; slotType: string }) =>
      held !== null &&
      dish.assignmentKey !== held.assignmentKey &&
      courseOfSlot(dish.slotType) === courseOfSlot(held.slotType),
    [held],
  );

  return { held, over, dragging, point, gripProps, canDropOn, cancel };
}
